import BaseRepository from "./BaseRepository";
import st from "lib/utils/index";
import { serializePricing } from "lib/utils/api/serializers";
import { ProductScheduleSlotDecorator, ProductScheduleSlotFullDecorator } from "lib/decorators/index";
import { saleWindowOffsetAttributes } from "lib/utils/saleWindow";

export default new (class ProductScheduleSlotsRepository extends BaseRepository {
  findAll(params = {}) {
    return this.api
      .get(`/product_schedule_slots`, { params })
      .map((response) => st.api.dejsonapinize(response.data.data, response.data.included))
      .map((slots) => slots.map((slot) => new ProductScheduleSlotDecorator(slot)));
  }

  find(id) {
    if (!id) return undefined;
    return this.api
      .get(`/product_schedule_slots/${id}`)
      .map((response) => st.api.dejsonapinize(response.data.data, response.data.included))
      .map((slot) => new ProductScheduleSlotFullDecorator(slot));
  }

  showManifest(id, format = "pdf") {
    const form = document.createElement("form");
    form.hidden = true;
    form.method = "POST";
    form.action = `${this.api.apiUrl}/product_schedule_slots/${id}/manifest.${format}?locale=${this.api.locale}&download=1`;
    form.target = "_blank";

    const adminUserEmailInput = document.createElement("input");
    adminUserEmailInput.name = "admin_user_email";
    adminUserEmailInput.value = this.api.currentAdminUserEmail;
    form.appendChild(adminUserEmailInput);

    const adminUserTokenInput = document.createElement("input");
    adminUserTokenInput.name = "admin_user_auth_token";
    adminUserTokenInput.value = this.api.currentAdminUserToken;
    form.appendChild(adminUserTokenInput);

    const currentCompanyIdInput = document.createElement("input");
    currentCompanyIdInput.name = "current_company_id";
    currentCompanyIdInput.value = this.api.currentCompanyId;
    form.appendChild(currentCompanyIdInput);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  create(slot) {
    const data = this._serializeSlotToData(slot, { withAllFutureSlots: true });
    return this.api
      .post(`/product_schedule_slots`, data)
      .map((response) => st.api.dejsonapinize(response.data.data, response.data.included))
      .map((slot) => new ProductScheduleSlotFullDecorator(slot));
  }

  update(slot, { withAllFutureSlots = true, withSaleWindowRule = true } = {}) {
    const data = this._serializeSlotToData(slot, { withAllFutureSlots, withSaleWindowRule });
    return this.api
      .put(`/product_schedule_slots/${slot.id}`, data)
      .map((response) => st.api.dejsonapinize(response.data.data, response.data.included))
      .map((slot) => new ProductScheduleSlotFullDecorator(slot));
  }

  delete(id, { withAllFutureSlots = true } = {}) {
    return this.api.delete(`/product_schedule_slots/${id}`, {
      data: {
        data: {
          with_all_future_slots: withAllFutureSlots,
        },
      },
    });
  }

  // `withSaleWindowRule` is off for forms that edit the concrete sale window
  // datetimes but not the rule behind them — the general reservations
  // calendar. They must not ship the offset their slot inherited from its
  // schedule, or the backend resolves it straight back over what was typed.
  _serializeSlotToData(slot, { withAllFutureSlots, withSaleWindowRule = true }) {
    return {
      data: {
        with_all_future_slots: !!slot.product_schedule && withAllFutureSlots,
        slot_attributes: {
          product_id: slot.product_id,
          product_variant_id: slot.product_variant_id,
          starts_at: slot.starts_at,
          ends_at: slot.ends_at,
          includes_time: slot.includes_time,
          active: slot.active,
          confirmed: slot.confirmed,
          availability_type: slot.availability_type,
          total_stock_limit: slot.total_stock_limit,
          note: slot.note,
          // Operator-editable sale window. Always sent so an explicit
          // nil-out also reaches the server.
          sale_starts_at: slot.sale_starts_at,
          sale_ends_at: slot.sale_ends_at,
        },
        // The relative half of the rule ("z wyprzedzeniem"). Sent outside
        // schedule_attributes on purpose: its presence must not make the
        // backend build a schedule for a slot added as a single date.
        sale_window_attributes: withSaleWindowRule ? saleWindowOffsetAttributes(slot) : undefined,
        schedule_attributes:
          !!slot.product_schedule && withAllFutureSlots
            ? {
                ...slot.product_schedule.recursion_attributes,
                time_duration: slot.time_duration,
              }
            : undefined,
        price_pricings_attributes: (slot.product_schedule || slot).price_pricings.map(serializePricing),
      },
    };
  }
})();



// WEBPACK FOOTER //
// ./src/lib/repositories/ProductScheduleSlotsRepository.js